import React from "react";
import styled from "styled-components";
import { Button, Dropdown } from "@fluentui/react-northstar";
import { ArrowDownload16Regular } from "@fluentui/react-icons";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";
import { DEPARTEMENTS, PERIODES } from "../../constants";
import { DEVICE_MAX } from "../../../../constants/responsive";
import {
    useQueryGetAllAgencies,
    useQueryGetAllManagers,
} from "../../../../api/helpers/queries";

/* Sentinelle "tous" côté dropdown ; stockée comme null (aucun param envoyé à l'API) */
const ALL = "all";
const withAll = (label, items) => [{ key: ALL, header: label }, ...items];

/** Barre de filtres sticky : période / agence / département / manager + export */
const FilterBar = ({ onExport }) => {
    const filters = usePilotageRhStore((s) => s.filters);
    const setFilter = usePilotageRhStore((s) => s.setFilter);
    const resetFilters = usePilotageRhStore((s) => s.resetFilters);

    const agencies = useQueryGetAllAgencies();
    const managers = useQueryGetAllManagers();

    const FILTERS = [
        { key: "period", label: "Période", items: PERIODES },
        {
            key: "agency_id",
            label: "Agence",
            loading: agencies.isLoading,
            items: withAll(
                "Toutes les agences",
                (agencies.data ?? []).map((a) => ({
                    key: a.id,
                    header: a.name,
                }))
            ),
        },
        {
            key: "department",
            label: "Département",
            items: withAll(
                "Tous les départements",
                DEPARTEMENTS.map((d) => ({ key: d, header: d }))
            ),
        },
        {
            key: "manager_id",
            label: "Manager",
            loading: managers.isLoading,
            items: withAll(
                "Tous les managers",
                (managers.data ?? []).map((m) => ({
                    key: m.id,
                    header: `${m.first_name} ${m.last_name}`,
                }))
            ),
        },
    ];

    return (
        <Bar>
            <Inner>
                {FILTERS.map((f) => (
                    <SDropdown
                        key={f.key}
                        aria-label={f.label}
                        items={f.items}
                        value={f.items.find(
                            (i) => i.key === (filters[f.key] ?? ALL)
                        )}
                        onChange={(_, data) =>
                            data?.value &&
                            setFilter(
                                f.key,
                                data.value.key === ALL ? null : data.value.key
                            )
                        }
                        loading={f.loading}
                        loadingMessage="Chargement…"
                        checkable
                        fluid
                    />
                ))}
                <Button
                    text
                    content="Réinitialiser les filtres"
                    onClick={resetFilters}
                    styles={{
                        color: "var(--prh-violet)",
                        textDecoration: "underline",
                        minWidth: 0,
                    }}
                />
                <ExportButton
                    secondary
                    flat
                    content="Exporter"
                    icon={<ArrowDownload16Regular />}
                    onClick={onExport}
                />
            </Inner>
        </Bar>
    );
};

export default FilterBar;

const Bar = styled.div`
    background: var(--prh-card);
    border-bottom: 1px solid var(--prh-line);
    position: sticky;
    top: 0;
    z-index: 50;
`;

const Inner = styled.div`
    max-width: var(--prh-max-width);
    margin: 0 auto;
    padding: 10px var(--prh-gutter);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media ${DEVICE_MAX.mobileL} {
        padding-inline: 16px;
    }
`;

const SDropdown = styled(Dropdown)`
    width: auto;
    min-width: 170px;
    .ui-dropdown__container {
        border-radius: var(--prh-radius-sm);
        border: 1px solid var(--prh-line-2);
        background: var(--prh-card);
        min-height: 34px;
    }
    .ui-dropdown__selected-items,
    .ui-button {
        font-size: 13px;
    }
    @media ${DEVICE_MAX.mobileL} {
        width: 100%;
    }
`;

const ExportButton = styled(Button)`
    margin-left: auto;
    border-color: var(--prh-violet);
    color: var(--prh-violet);
    border-radius: var(--prh-radius-sm);
    &:hover {
        background: var(--prh-violet-bg);
    }
`;
